import { cn } from 'utils/helpers';
import { formatCurrency, TotalType, SUBTOTAL_ROW, TOTAL_ROWS, calculateTotalFromRows } from '../../../components/QuoteTable/utils';
import type { QuoteTableRow } from '../../../components/QuoteTable/quote-table.types';
import type { QuoteTableColumn, QuoteColumnType } from '../../../components/QuoteTable/quote-table.types';

// ── Cell-based edit values (normalised column structure) ──────

export interface CellEditValues {
    [rowId: number]: {
        [columnId: number]: string;
    };
}

// ── Props ─────────────────────────────────────────────────────

export interface QuoteTableEditProps {
    className?: string;
    columns?: QuoteTableColumn[];
    diffKeys?: Set<string>;
    disabled?: boolean;
    editCellValues?: CellEditValues;
    editValues: Record<number, { quantity: string; rate: string }>;
    previousSubtotal?: number;
    rows: QuoteTableRow[];
    showAllTotals?: boolean;
    showSubTotal?: boolean;
    showUnitColumn?: boolean;
    onValueChange: (itemId: number, field: 'quantity' | 'rate', value: string) => void;
    onCellValueChange?: (rowId: number, columnId: number, value: string) => void;
}

const HEADERS_WITH_UNIT = [
    { name: '#', width: '3%' },
    { name: 'Description of Scope', width: '40%' },
    { name: 'Unit of Measurement', width: '19%' },
    { name: 'Quantity', width: '12%' },
    { name: 'Rate', width: '12%' },
    { name: 'Cost', width: '12%' },
];

const HEADERS_WITHOUT_UNIT = [
    { name: '#', width: '5%' },
    { name: 'Description', width: '35%' },
    { name: 'Quantity', width: '20%' },
    { name: 'Rate', width: '20%' },
    { name: 'Cost', width: '20%' },
];

const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === '-') {
        event.preventDefault();
    }
};

const cellBorder = 'border border-[#EAECF0]';

const COLUMN_STYLE = 'pl-2 font-roboto text-sm bg-gray-50';

const EDITABLE_COLUMN_TYPES: Set<QuoteColumnType> = new Set([
    'response_quantity',
    'response_value',
    'response_number',
    'response_text',
]);

const NUMERIC_COLUMN_TYPES: Set<QuoteColumnType> = new Set([
    'response_quantity',
    'response_value',
    'response_number',
]);

const isReadOnlyColumnType = (type: QuoteColumnType): boolean =>
    type === 'description' || type === 'unit_of_measurement' || type === 'total';

const getInputType = (columnType: QuoteColumnType): string =>
    NUMERIC_COLUMN_TYPES.has(columnType) ? 'number' : 'text';

// ── Column-based (normalised) table ───────────────────────────

interface ColumnTableProps {
    className?: string;
    columns: QuoteTableColumn[];
    diffKeys?: Set<string>;
    disabled: boolean;
    editCellValues?: CellEditValues;
    rows: QuoteTableRow[];
    onCellValueChange?: (rowId: number, columnId: number, value: string) => void;
}

const ColumnBasedTable: React.FC<ColumnTableProps> = ({
    columns,
    rows,
    editCellValues,
    onCellValueChange,
    diffKeys,
    disabled,
    className,
}) => {
    const sortedColumns = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);

    return (
        <table
            className={cn(
                'w-full table-auto rounded-xl border-b border-solid border-[#EAECF0] bg-white',
                className
            )}
            data-testid="quote-table-edit"
        >
            <thead className="h-[44px] bg-[#F9FAFB]">
                <tr className="border-b border-[#EAECF0]">
                    <th
                        className="bg-[#F9FAFB] font-roboto text-[12px] font-medium text-tertiary-600 text-center"
                        style={{ width: '3%' }}
                    >
                        <div className="mt-[3px]">#</div>
                    </th>
                    {sortedColumns.map((col) => (
                        <th
                            key={col.id}
                            className="bg-[#F9FAFB] font-roboto text-[12px] font-medium text-tertiary-600 text-left"
                        >
                            <div className="md:ml-1 lg:ml-1 mt-[3px]">{col.label}</div>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, idx) => (
                    <tr
                        key={row.itemId}
                        className="border-b border-[#EAECF0]"
                    >
                        <td
                            className={cn(
                                cellBorder,
                                'text-center font-roboto font-normal text-primary-500 text-xs',
                                'bg-gray-50'
                            )}
                            style={{ height: '35px' }}
                        >
                            {idx + 1}
                        </td>
                        {sortedColumns.map((col) => {
                            const cellValue = col.columnType === 'description'
                                ? row.name
                                : col.columnType === 'unit_of_measurement'
                                    ? (row.unitLabel ?? '')
                                    : (editCellValues?.[row.itemId]?.[col.id]
                                        ?? row.columnValues?.[col.id]
                                        ?? '');
                            const diffKey = `cell-${row.itemId}-${col.id}`;
                            const isDiff = diffKeys?.has(diffKey) ?? false;

                            if (isReadOnlyColumnType(col.columnType)) {
                                return (
                                    <td
                                        key={col.id}
                                        className={cn(
                                            cellBorder,
                                            'text-gray-900',
                                            COLUMN_STYLE,
                                            isDiff && 'bg-blue-50 ring-2 ring-inset ring-blue-300'
                                        )}
                                        data-testid={`cell-${row.itemId}-${col.id}`}
                                    >
                                        {col.columnType === 'total'
                                            ? formatCurrency(Number(cellValue) || 0)
                                            : (cellValue || '-')}
                                    </td>
                                );
                            }

                            if (EDITABLE_COLUMN_TYPES.has(col.columnType)) {
                                return (
                                    <td
                                        key={col.id}
                                        className={cn(
                                            cellBorder,
                                            isDiff && 'bg-blue-50 ring-2 ring-inset ring-blue-300'
                                        )}
                                    >
                                        <input
                                            type={getInputType(col.columnType)}
                                            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                            value={cellValue}
                                            onChange={(e) => onCellValueChange?.(row.itemId, col.id, e.target.value)}
                                            onKeyDown={NUMERIC_COLUMN_TYPES.has(col.columnType) ? handleKeyDown : undefined}
                                            min={NUMERIC_COLUMN_TYPES.has(col.columnType) ? 0 : undefined}
                                            disabled={disabled || !(col.isSupplierEditable ?? true)}
                                            data-testid={`cell-input-${row.itemId}-${col.id}`}
                                        />
                                    </td>
                                );
                            }

                            // Fallback for unknown column types
                            return (
                                <td
                                    key={col.id}
                                    className={cn(cellBorder, 'text-gray-900', COLUMN_STYLE)}
                                >
                                    {cellValue || '-'}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

// ── Main component ────────────────────────────────────────────

const QuoteTableEdit: React.FC<QuoteTableEditProps> = ({
    rows,
    editValues,
    onValueChange,
    columns,
    editCellValues,
    onCellValueChange,
    diffKeys,
    previousSubtotal,
    showAllTotals = false,
    showSubTotal = false,
    showUnitColumn = false,
    disabled = false,
    className,
}) => {
    // If columns are provided, render the normalised column-based table
    if (columns && columns.length > 0) {
        return (
            <ColumnBasedTable
                columns={columns}
                rows={rows}
                editCellValues={editCellValues}
                onCellValueChange={onCellValueChange}
                diffKeys={diffKeys}
                disabled={disabled}
                className={className}
            />
        );
    }

    // Legacy rendering path
    const headers = showUnitColumn ? HEADERS_WITH_UNIT : HEADERS_WITHOUT_UNIT;
    const spacerColSpan = headers.length - 2;
    const currentSubtotal = calculateTotalFromRows(rows, TotalType.SubTotal);
    const isSubtotalDiff = previousSubtotal != null && currentSubtotal !== previousSubtotal;

    return (
        <table
            className={cn(
                'w-full table-auto rounded-xl border-b border-solid border-[#EAECF0] bg-white',
                className
            )}
            data-testid="quote-table-edit"
        >
            <thead className="h-[44px] bg-[#F9FAFB]">
                <tr className="border-b border-[#EAECF0]">
                    {headers.map((th, index) => (
                        <th
                            key={th.name}
                            className={cn("bg-[#F9FAFB] font-roboto text-[12px] font-medium text-tertiary-600",
                                index === 0 ? 'text-center' : 'text-left')}
                            style={{ width: th.width }}
                        >
                            <div className={cn(index === 0 ? 'ml-0' : "md:ml-1 lg:ml-1", "mt-[3px]")}>{th.name}</div>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, idx) => {
                    const qty = editValues[row.itemId]?.quantity ?? String(row.quantity);
                    const rate = editValues[row.itemId]?.rate ?? String(row.rate);
                    const cost = Number(qty) * Number(rate);
                    const isQtyDiff = diffKeys?.has(`q-${row.itemId}.quantity`) ?? false;
                    const isRateDiff = diffKeys?.has(`q-${row.itemId}.rate`) ?? false;
                    const isCostDiff = diffKeys?.has(`q-${row.itemId}.cost`) ?? false;
                    const isPercentage = row.unitLabel?.indexOf('%') > -1;

                    return (
                        <tr
                            key={row.itemId}
                            className="border-b border-[#EAECF0]"
                        >
                            <td
                                className={cn(
                                    cellBorder,
                                    'text-center font-roboto font-normal text-primary-500 text-xs',
                                    'bg-gray-50'
                                )}
                                style={{ height: '35px' }}
                            >
                                {idx + 1}
                            </td>
                            <td
                                className={cn(
                                    cellBorder,
                                    'text-gray-900',
                                    COLUMN_STYLE
                                )}
                            >
                                {row.name}
                            </td>
                            {showUnitColumn && (
                                <td
                                    className={cn(
                                        cellBorder,
                                        'text-gray-900',
                                        COLUMN_STYLE
                                    )}
                                >
                                    {row.unitLabel ?? '-'}
                                </td>
                            )}
                            <td
                                className={cn(
                                    cellBorder,
                                    isQtyDiff && 'bg-blue-50 ring-2 ring-inset ring-blue-300'
                                )}
                            >
                                <input
                                    type="number"
                                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                    value={qty}
                                    onChange={(e) => onValueChange(row.itemId, 'quantity', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    min={0}
                                    disabled={disabled}
                                    data-testid={`edit-qty-${row.itemId}`}
                                />
                            </td>
                            <td
                                className={cn(
                                    cellBorder,
                                    !isPercentage && isRateDiff && 'bg-blue-50 ring-2 ring-inset ring-blue-300'
                                )}
                            >
                                {!isPercentage && <input
                                    type="number"
                                    className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                    value={rate}
                                    onChange={(e) => onValueChange(row.itemId, 'rate', e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    min={0}
                                    disabled={disabled}
                                    data-testid={`edit-rate-${row.itemId}`}
                                />}
                            </td>
                            <td
                                className={cn(
                                    cellBorder,
                                    'text-gray-900',
                                    COLUMN_STYLE,
                                    !isPercentage && isCostDiff && 'bg-blue-50 ring-2 ring-inset ring-blue-300'
                                )}
                            >
                                {isPercentage ? '' : formatCurrency(isNaN(cost) ? 0 : cost)}
                            </td>
                        </tr>
                    );
                })}
                {showAllTotals &&
                    TOTAL_ROWS.map((total) => {
                        const isTotalDiff = isSubtotalDiff && total.id === TotalType.SubTotal;
                        return (
                            <tr key={total.id}>
                                <td
                                    colSpan={spacerColSpan}
                                    className="h-[48px] border-none"
                                    style={{ background: '#F9FAFB' }}
                                />
                                <td
                                    className={cn(cellBorder, 'bg-gray-utility-100 px-4 py-2')}
                                    style={{ color: '#D0D5DD' }}
                                >
                                    <div
                                        className="font-roboto font-medium"
                                        style={{ fontSize: '12px', color: total.color }}
                                    >
                                        {total.label}
                                    </div>
                                </td>
                                <td
                                    className={cn(
                                        cellBorder,
                                        'bg-gray-utility-100 px-4 py-2',
                                        isTotalDiff && 'bg-blue-50 ring-2 ring-inset ring-blue-300'
                                    )}
                                    style={{ color: '#333' }}
                                    data-testid={isTotalDiff ? 'subtotal-diff' : undefined}
                                >blah
                                    {formatCurrency(calculateTotalFromRows(rows, total.id))}
                                </td>
                            </tr>
                        );
                    })}
                {showSubTotal && !showAllTotals && (
                    <tr key={SUBTOTAL_ROW.id}>
                        <td
                            colSpan={spacerColSpan}
                            className="h-[48px] border-none"
                            style={{ background: '#F9FAFB' }}
                        />
                        <td
                            className={cn(cellBorder, 'bg-gray-utility-100 px-4 py-2')}
                            style={{ color: '#D0D5DD' }}
                        >
                            <div
                                className="font-roboto font-medium"
                                style={{ fontSize: '12px', color: SUBTOTAL_ROW.color }}
                            >
                                {SUBTOTAL_ROW.label}
                            </div>
                        </td>
                        <td
                            className={cn(
                                cellBorder,
                                'bg-gray-utility-100 px-4 py-2',
                                isSubtotalDiff && 'bg-blue-50 ring-2 ring-inset ring-blue-300'
                            )}
                            style={{ color: '#333' }}
                            data-testid={isSubtotalDiff ? 'subtotal-diff' : undefined}
                        >
                            {formatCurrency(calculateTotalFromRows(rows, SUBTOTAL_ROW.id))}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
};

export { QuoteTableEdit };
